import { useEffect } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { U2000FormStateHandlers } from '../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { NS } from '../../../U3206';
import type { U3206AiUserGroupFormRow } from '../../../U3206Service';
import { u3206FormatUserLabel } from '../../../U3206Service';
import { useU3206TokenUsageHandlers } from '../../../hooks/useU3206TokenUsageHandlers';

interface U3206TokenUsageTabProps {
  formStateHandlers: U2000FormStateHandlers<U3206AiUserGroupFormRow>;
  t: TFunction;
  isActive: boolean;
}

function formatTokenCount(value: number, locale: string): string {
  return new Intl.NumberFormat(locale).format(value);
}

function U3206TokenUsageTab({ formStateHandlers, t, isActive }: U3206TokenUsageTabProps) {
  const { i18n } = useTranslation(NS);
  const data = formStateHandlers.getData();
  const groupId = data?.id?.trim() ?? '';
  const { report, loading, loadTokenUsage, clearTokenUsage } = useU3206TokenUsageHandlers();

  useEffect(() => {
    if (!isActive) return;
    if (!groupId) {
      clearTokenUsage();
      return;
    }
    void loadTokenUsage(groupId);
  }, [clearTokenUsage, groupId, isActive, loadTokenUsage]);

  if (!groupId) {
    return (
      <p className="rounded-lg border border-dashed border-border bg-muted/30 p-4 text-sm text-muted-foreground">
        {t('tokenUsageSaveFirst', { ns: NS })}
      </p>
    );
  }

  if (loading && !report) {
    return <p className="text-sm text-muted-foreground">{t('tokenUsageLoading', { ns: NS })}</p>;
  }

  if (!report || report.providers.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('tokenUsageNoProviders', { ns: NS })}</p>;
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold">{t('tokenUsageTitle', { ns: NS })}</h3>
        <p className="text-xs text-muted-foreground">
          {t('tokenUsagePeriod', {
            ns: NS,
            start: report.period.start,
            end: report.period.end,
            timezone: report.period.timezone,
          })}
        </p>
      </div>

      {report.providers.map((provider) => {
        const usageLabel =
          provider.groupQuotaMonthlyTokens != null
            ? t('tokenUsageGroupTotal', {
                ns: NS,
                usage: formatTokenCount(provider.groupUsageTokens, i18n.language),
                quota: formatTokenCount(provider.groupQuotaMonthlyTokens, i18n.language),
              })
            : t('tokenUsageGroupUnlimited', {
                ns: NS,
                usage: formatTokenCount(provider.groupUsageTokens, i18n.language),
              });

        return (
          <div key={provider.providerId} className="rounded-lg border border-border p-3">
            <p className="text-sm font-medium">
              {provider.code} — {provider.name}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">{usageLabel}</p>
            {provider.members.length > 0 ? (
              <ul className="mt-2 space-y-1 border-t border-border pt-2">
                {provider.members.map((member) => {
                  const label = u3206FormatUserLabel({
                    username: member.username ?? member.userId,
                    firstName: member.firstName ?? '',
                    lastName: member.lastName ?? '',
                  });
                  return (
                    <li key={member.userId} className="text-xs text-muted-foreground">
                      {t('tokenUsageMemberUsage', {
                        ns: NS,
                        label,
                        usage: formatTokenCount(member.usageTokens, i18n.language),
                      })}
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export default U3206TokenUsageTab;
